import { z } from "zod";

// === Entity schemas ===

export const OAuthAuthorizationCodeSchema = z.object({
  code: z.string().describe("Code"),
  user_id: z.string().uuid().describe("User"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  client_id: z.string().describe("Client"),
  redirect_uri: z.string().url().describe("Redirect URI"),
  scope: z.string().nullable().describe("Scope"),
  expires_at: z.string().describe("Expires At"),
  consumed_at: z.string().nullable().describe("Consumed At"),
  created_at: z.string().describe("Created At"),
});
export type OAuthAuthorizationCode = z.infer<
  typeof OAuthAuthorizationCodeSchema
>;

export const OAuthRefreshTokenSchema = z.object({
  token_hash: z.string().describe("Token Hash"),
  user_id: z.string().uuid().describe("User"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  client_id: z.string().describe("Client"),
  expires_at: z.string().describe("Expires At"),
  revoked_at: z.string().nullable().describe("Revoked At"),
  last_used_at: z.string().nullable().describe("Last Used At"),
  created_at: z.string().describe("Created At"),
});
export type OAuthRefreshToken = z.infer<typeof OAuthRefreshTokenSchema>;

// === Request/response schemas ===

export const CreateAuthorizationCodeRequestSchema = z.object({
  client_id: z.string().min(1),
  redirect_uri: z.string().url(),
  scope: z.string().optional(),
  state: z.string().optional(),
});
export type CreateAuthorizationCodeRequest = z.infer<
  typeof CreateAuthorizationCodeRequestSchema
>;

export const CreateAuthorizationCodeResponseSchema = z.object({
  code: z.string(),
  expires_at: z.string(),
});
export type CreateAuthorizationCodeResponse = z.infer<
  typeof CreateAuthorizationCodeResponseSchema
>;

export const AuthorizationCodeGrantRequestSchema = z.object({
  grant_type: z.literal("authorization_code"),
  code: z.string().min(1),
  client_id: z.string().min(1),
  redirect_uri: z.string().url(),
});
export type AuthorizationCodeGrantRequest = z.infer<
  typeof AuthorizationCodeGrantRequestSchema
>;

export const RefreshTokenGrantRequestSchema = z.object({
  grant_type: z.literal("refresh_token"),
  refresh_token: z.string().min(1),
});
export type RefreshTokenGrantRequest = z.infer<
  typeof RefreshTokenGrantRequestSchema
>;

export const TokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal("Bearer"),
  expires_in: z.number().int().positive(),
  refresh_token: z.string().optional(),
  user: z.object({
    id: z.string().uuid(),
    // schema-check-ignore: response-shape — OAuth token response from external IdP; not a CSV/import input
    email: z.string().email().optional(),
  }),
});
export type TokenResponse = z.infer<typeof TokenResponseSchema>;
